import { reflect } from '@effector/reflect';
import { useUnit } from 'effector-react';
import { Trash2 } from 'lucide-react';
import { not } from 'patronum';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { translated } from 'shared/lib';
import { ConfirmModalProps, createConfirmModal } from 'shared/ui';
import { DeleteCashdeskModel } from '../../model';

export const createDeleteCashdeskModal = (model: DeleteCashdeskModel) => {
	const ConfirmModal = createConfirmModal(model);

	const DeleteCashdeskModal = forwardRef<HTMLDivElement, ConfirmModalProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [cashdesk] = useUnit([model.$modalData]);

		return (
			<ConfirmModal
				ref={ref}
				title={t('delete-cashdesk.title')}
				cancelButtonProps={{
					children: t('delete-cashdesk.cancel'),
				}}
				submitButtonProps={{
					children: t('delete-cashdesk.delete'),
					variant: 'destructive',
				}}
				{...props}
			>
				{t('delete-cashdesk.description', { name: cashdesk?.name })}
			</ConfirmModal>
		);
	});

	const DeleteCashdeskModalButton = reflect({
		view: ConfirmModal.Button,
		bind: {
			'data-test-id': 'delete-cashdesk-button',
			variant: 'destructive',
			disabled: not(viewerModel.$canWriteCashdesks),
			leftSection: <Trash2 />,
			children: translated('delete-cashdesk.delete'),
		},
	});

	const DeleteCashdeskModalActionIcon = reflect({
		view: ConfirmModal.ActionIcon,
		bind: {
			'data-test-id': 'delete-cashdesk-action-icon',
			variant: 'ghost-destructive',
			disabled: not(viewerModel.$canWriteCashdesks),
			children: <Trash2 />,
		},
	});

	return Object.assign(DeleteCashdeskModal, {
		Button: DeleteCashdeskModalButton,
		ActionIcon: DeleteCashdeskModalActionIcon,
	});
};
